import { Box, Chip, Stack, Typography } from "@mui/material"
import { FC } from "react"
import { SnapshotPairDto } from "./useReservationSyncReport"

/**
 * Keys whose VALUES are sensitive: locker access codes, payment identifiers, and
 * the customer's name.
 *
 * They are stored verbatim rather than redacted, because the revert script needs
 * the exact prior value to restore and a redacted code is an unrevertable field.
 * Marking them here is the reader-facing half of that trade: the compensating
 * controls (KMS, no public access, lifecycle expiry, an admin-log entry per read)
 * live on the bucket and the API.
 */
const SENSITIVE = new Set([
  "delivery_code",
  "reception_code",
  "admin_code",
  "card_id",
  "payment_receipt_id",
  "user_id",
  "firstName",
  "lastName",
])

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v)

const Line: FC<{
  k: string
  v: unknown
  depth: number
  changed: boolean
}> = ({ k, v, depth, changed }) => {
  const sensitive = SENSITIVE.has(k)
  return (
    <Box sx={{ pl: depth * 1.5 }}>
      <Box component="span" sx={{ color: "text.disabled" }}>
        {k}:{" "}
      </Box>
      <Box
        component="span"
        sx={{
          ...(sensitive
            ? { color: "error.main", fontWeight: 600 }
            : changed
              ? { color: "warning.main", fontWeight: 600 }
              : {}),
        }}
      >
        {isObject(v) || Array.isArray(v) ? "" : JSON.stringify(v)}
      </Box>
      {isObject(v) &&
        Object.entries(v).map(([ck, cv]) => (
          <Line key={ck} k={ck} v={cv} depth={depth + 1} changed={false} />
        ))}
    </Box>
  )
}

const Panel: FC<{
  title: string
  snapshot: Record<string, unknown> | null
  changed: Set<string>
  absentLabel: string
}> = ({ title, snapshot, changed, absentLabel }) => (
  <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, flex: 1, minWidth: 0 }}>
    <Typography
      sx={{
        px: 1.5,
        py: 0.75,
        fontSize: "0.68rem",
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "text.secondary",
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      {title}
    </Typography>
    <Box
      sx={{
        p: 1.5,
        fontFamily: "monospace",
        fontSize: "0.72rem",
        maxHeight: 340,
        overflow: "auto",
      }}
    >
      {snapshot ? (
        Object.entries(snapshot).map(([k, v]) => (
          <Line key={k} k={k} v={v} depth={0} changed={changed.has(k)} />
        ))
      ) : (
        <Typography variant="caption" sx={{ color: "error.main" }}>
          {absentLabel}
        </Typography>
      )}
    </Box>
  </Box>
)

/** Side-by-side partner-vs-ours, for one reservation, fetched on demand. */
export const SnapshotPairView: FC<{
  pair: SnapshotPairDto
  fromKey?: string
  /** True when the part was already held, so opening this row cost no request. */
  cached?: boolean
  apiVersion: "v1" | "v2"
}> = ({ pair, fromKey, cached, apiVersion }) => {
  const ourSnapshot =
    (pair.ours?.sstReservation as Record<string, unknown> | undefined) ?? null
  const partner = pair.partner ?? null
  const changed = new Set<string>()
  const keys = new Set([
    ...Object.keys(partner ?? {}),
    ...Object.keys(ourSnapshot ?? {}),
  ])
  keys.forEach((k) => {
    if (
      JSON.stringify((partner ?? {})[k]) !==
      JSON.stringify((ourSnapshot ?? {})[k])
    )
      changed.add(k)
  })

  return (
    <Box sx={{ p: 1.5, bgcolor: "action.hover" }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
          {fromKey ?? "—"}
        </Typography>
        {/*
          The old label said "fetched for this row only", which was never true and
          is now actively wrong: the part holds every row in its id range, and a
          part already held serves further rows with no request at all.
        */}
        <Chip
          size="small"
          variant="outlined"
          color={cached ? "success" : "default"}
          label={
            cached
              ? "from a part already loaded — no request"
              : "part fetched once; its other rows are now free"
          }
        />
      </Stack>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
        <Panel
          title={`Partner (SST ${apiVersion}) — raw reservation`}
          snapshot={partner}
          changed={changed}
          absentLabel="the partner has no such reservation"
        />
        <Panel
          title="Ours — the stored snapshot"
          snapshot={ourSnapshot}
          changed={changed}
          absentLabel="no row on our side — the partner has this reservation and we never stored it"
        />
      </Stack>
      <Typography variant="caption" sx={{ display: "block", mt: 1, color: "text.secondary" }}>
        <Box component="span" sx={{ color: "error.main", fontWeight: 600 }}>
          Red keys are sensitive
        </Box>{" "}
        — access codes, payment ids, customer name. Stored verbatim because the revert
        needs the exact prior value to restore; the object is encrypted, never public,
        expires on a lifecycle rule, and this read was written to the admin log. Amber
        keys differ between the two sides.
      </Typography>
    </Box>
  )
}
